(() => {
  'use strict';
  const controllers = new Map();
  const groups = [
    { selector: '.mr-native-tour .feature-tabs, .mr-native-tour .day-tabs', width: 760 },
    { selector: '.mr-about-root .mr-about-page-nav nav', width: 850 },
    { selector: '.mr-experience .mrte-chapters > .mrte-wrap', width: 800 }
  ];
  const selected = '[aria-selected="true"], [aria-current="location"]';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  function mount(rail, width) {
    if (controllers.has(rail)) return;
    const items = Array.from(rail.children).filter(item => item.matches('a, button'));
    if (items.length < 2) return;
    const mobile = matchMedia(`(max-width: ${width}px)`);
    const controller = new AbortController();
    const { signal } = controller;
    let lastActive;
    let pendingLink;
    let pendingTimer;
    let frame;
    let startPadding = -1;
    let endPadding = -1;
    const active = () => items.find(item => item.matches(selected)) || items[0];

    function center(item, animate) {
      if (!mobile.matches || !rail.clientWidth || !item) return;
      const box = rail.getBoundingClientRect();
      const tab = item.getBoundingClientRect();
      const left = rail.scrollLeft + tab.left + tab.width / 2 - box.left - rail.clientLeft - rail.clientWidth / 2;
      rail.scrollTo({ left, behavior: animate && !reduced.matches ? 'smooth' : 'instant' });
    }

    function sync() {
      const item = active();
      if (pendingLink && item !== pendingLink) return;
      if (pendingLink) {
        pendingLink = null;
        clearTimeout(pendingTimer);
      }
      if (item === lastActive) return;
      lastActive = item;
      center(item, true);
    }

    function measure() {
      frame = null;
      if (!mobile.matches) {
        rail.removeAttribute('data-mr-centered-tabs');
        rail.style.removeProperty('--mr-tabs-start');
        rail.style.removeProperty('--mr-tabs-end');
        startPadding = endPadding = -1;
        return;
      }
      rail.setAttribute('data-mr-centered-tabs', 'true');
      if (!rail.clientWidth) return;
      const start = Math.max(0, (rail.clientWidth - items[0].getBoundingClientRect().width) / 2);
      const end = Math.max(0, (rail.clientWidth - items[items.length - 1].getBoundingClientRect().width) / 2);
      if (start !== startPadding) {
        rail.style.setProperty('--mr-tabs-start', `${start}px`);
        startPadding = start;
      }
      if (end !== endPadding) {
        rail.style.setProperty('--mr-tabs-end', `${end}px`);
        endPadding = end;
      }
      lastActive = pendingLink || active();
      center(lastActive, false);
    }

    function scheduleMeasure() {
      if (!frame && !signal.aborted) frame = requestAnimationFrame(measure);
    }

    rail.addEventListener('click', event => {
      const item = event.target.closest('a, button');
      if (!mobile.matches || !items.includes(item)) return;
      if (item.matches('a[href^="#"]') && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        pendingLink = item;
        clearTimeout(pendingTimer);
        pendingTimer = setTimeout(() => { pendingLink = null; sync(); }, 1600);
      }
      lastActive = item;
      center(item, true);
    }, { signal });

    const observer = new MutationObserver(sync);
    items.forEach(item => observer.observe(item, { attributes: true, attributeFilter: ['aria-selected', 'aria-current'] }));
    const resize = 'ResizeObserver' in window ? new ResizeObserver(scheduleMeasure) : null;
    resize?.observe(rail);
    items.forEach(item => resize?.observe(item));
    window.addEventListener('resize', scheduleMeasure, { passive: true, signal });
    window.addEventListener('pageshow', scheduleMeasure, { signal });
    document.fonts?.ready.then(scheduleMeasure);
    controllers.set(rail, () => {
      controller.abort();
      observer.disconnect();
      resize?.disconnect();
      clearTimeout(pendingTimer);
      cancelAnimationFrame(frame);
      controllers.delete(rail);
    });
    measure();
  }

  function init(scope = document) {
    groups.forEach(({ selector, width }) => {
      if (scope.matches?.(selector)) mount(scope, width);
      scope.querySelectorAll(selector).forEach(rail => mount(rail, width));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach((destroy, rail) => { if (event.target.contains(rail)) destroy(); });
  });
})();
